import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ApiError } from '@fetmode/api-client';
import { useCallback, useEffect, useMemo, useState, } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import { resolveAppUrl } from '../urls.js';
import { useTesterPanel } from './TesterPanelProvider.js';
function stepHref(step, variant) {
    if (step.app === 'any' || step.app === variant) {
        if (step.id === 'cams_browse' || step.id === 'find_model' || step.id === 'join_room') {
            return '/cams';
        }
        if (step.id === 'studio_login' || step.id === 'go_live' || step.id === 'end_or_away') {
            return '/live';
        }
        if (step.id === 'check_balance' || step.id === 'coin_shop') {
            return '/';
        }
        return null;
    }
    const env = typeof import.meta !== 'undefined' && import.meta.env
        ? import.meta.env
        : {};
    if (step.app === 'web') {
        return resolveAppUrl('consumer', env);
    }
    return resolveAppUrl('studio', env);
}
function progressForStep(progress, stepId) {
    return progress.find((entry) => entry.stepId === stepId)?.status ?? 'PENDING';
}
function latestCommentForStep(feedback, stepId) {
    const match = feedback.find((entry) => entry.stepId === stepId && entry.comment);
    return match?.comment ?? '';
}
function sectionDoneCount(section, progress) {
    return section.items.filter((item) => progressForStep(progress, item.id) === 'DONE').length;
}
function useNarrowViewport() {
    const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' ? window.matchMedia('(max-width: 720px)').matches : false);
    useEffect(() => {
        const media = window.matchMedia('(max-width: 720px)');
        const sync = () => {
            setNarrow(media.matches);
        };
        sync();
        media.addEventListener('change', sync);
        return () => {
            media.removeEventListener('change', sync);
        };
    }, []);
    return narrow;
}
export function TesterPanel({ variant }) {
    const { api } = useAuth();
    const { session, isLoading, isMinimized, setMinimized, refreshSession } = useTesterPanel();
    const narrow = useNarrowViewport();
    const location = useLocation();
    const [sectionIndex, setSectionIndex] = useState(0);
    const [drafts, setDrafts] = useState({});
    const [busyStepId, setBusyStepId] = useState(null);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const sections = session?.sections ?? [];
    useEffect(() => {
        if (!session) {
            return;
        }
        setDrafts((prev) => {
            const next = { ...prev };
            for (const step of session.steps) {
                if (next[step.id] === undefined) {
                    next[step.id] = latestCommentForStep(session.feedback, step.id);
                }
            }
            return next;
        });
    }, [session]);
    useEffect(() => {
        if (!session?.sections.length) {
            return;
        }
        const firstIncomplete = session.sections.findIndex((section) => section.items.some((item) => progressForStep(session.progress, item.id) !== 'DONE'));
        if (firstIncomplete >= 0) {
            setSectionIndex(firstIncomplete);
        }
    }, [session?.sessionId]);
    const currentSection = sections[sectionIndex] ?? null;
    const missionDone = useMemo(() => {
        if (!session) {
            return 0;
        }
        const ids = new Set(session.steps.map((step) => step.id));
        return session.progress.filter((entry) => ids.has(entry.stepId) && entry.status === 'DONE').length;
    }, [session]);
    const saveItem = useCallback(async (step, options) => {
        const comment = (drafts[step.id] ?? '').trim();
        if (options.requireComment && !comment) {
            setError('Add a comment for this item before continuing.');
            return;
        }
        setBusyStepId(step.id);
        setError(null);
        setMessage(null);
        try {
            if (comment) {
                await api.submitTesterFeedback({
                    stepId: step.id,
                    comment,
                    app: variant,
                    path: location.pathname + location.search,
                    markDone: options.markDone,
                });
            }
            else if (options.markDone) {
                await api.completeTesterStep(step.id);
            }
            else {
                setError('Nothing to save.');
                return;
            }
            await refreshSession();
            setMessage(options.markDone ? 'Saved — item marked done.' : 'Comment saved.');
        }
        catch (err) {
            setError(err instanceof ApiError ? err.message : 'Could not save');
        }
        finally {
            setBusyStepId(null);
        }
    }, [api, drafts, location.pathname, location.search, refreshSession, variant]);
    if (isLoading || !session || sections.length === 0) {
        return null;
    }
    const otherAppLabel = variant === 'web' ? 'Studio' : 'Consumer';
    const totalSteps = session.steps.length;
    const panel = (_jsxs("div", { className: "fm-tester-panel", style: {
            position: 'fixed',
            zIndex: isMinimized ? 30 : 9000,
            width: isMinimized ? 'auto' : 'min(26rem, calc(100vw - 2rem))',
            maxWidth: isMinimized && narrow ? 'min(10.5rem, calc(100vw - 13rem))' : undefined,
            maxHeight: isMinimized ? 'auto' : 'min(82vh, 40rem)',
            overflow: isMinimized ? 'hidden' : 'auto',
            // Minimized chip stays left and above the bottom dock so it cannot cover Continue.
            left: isMinimized && narrow ? '0.75rem' : undefined,
            right: isMinimized && narrow ? 'auto' : '1rem',
            bottom: isMinimized
                ? 'calc(0.75rem + var(--fm-dock-height, 0px) + env(safe-area-inset-bottom, 0px))'
                : '1rem',
            background: 'var(--fm-color-surface)',
            border: '1px solid var(--fm-color-border)',
            borderRadius: '10px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
            fontSize: '0.875rem',
        }, children: [_jsxs("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.5rem',
                    padding: '0.75rem 1rem',
                    borderBottom: isMinimized ? 'none' : '1px solid var(--fm-color-border)',
                }, children: [_jsx("strong", { style: { fontFamily: 'var(--fm-font-heading)' }, children: isMinimized && narrow ? 'Guide' : 'Alpha test matrix' }), _jsx("button", { type: "button", onClick: () => {
                            setMinimized(!isMinimized);
                        }, style: {
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--fm-color-link)',
                            cursor: 'pointer',
                            font: 'inherit',
                        }, children: isMinimized ? 'Open' : 'Minimize' })] }), !isMinimized && currentSection ? (_jsxs("div", { style: { padding: '0.75rem 1rem 1rem' }, children: [_jsx("p", { style: {
                            margin: '0 0 0.75rem',
                            padding: '0.55rem 0.65rem',
                            borderRadius: '8px',
                            border: '1px solid color-mix(in srgb, var(--fm-color-secondary) 35%, var(--fm-color-border))',
                            background: 'color-mix(in srgb, var(--fm-color-secondary) 8%, var(--fm-color-surface))',
                            color: 'var(--fm-color-text-muted)',
                            fontSize: '0.8125rem',
                            lineHeight: 1.45,
                        }, children: "Soft POC \u2014 not production. Expect slower load, room join, and chat than a live cam site; video uses a demo mesh (no production SFU/TURN yet). Payments and age verify are stubs. Still mark freezes and crashes honestly; that is the useful signal." }), _jsxs("p", { style: { margin: '0 0 0.35rem', color: 'var(--fm-color-text-muted)' }, children: [missionDone, "/", totalSteps, " items \u00B7 section ", sectionIndex + 1, "/", sections.length, " \u00B7", ' ', session.intendedRole] }), _jsx("div", { style: {
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.35rem',
                            marginBottom: '0.75rem',
                        }, children: sections.map((section, index) => {
                            const done = sectionDoneCount(section, session.progress);
                            const active = index === sectionIndex;
                            return (_jsxs("button", { type: "button", onClick: () => {
                                    setSectionIndex(index);
                                    setMessage(null);
                                    setError(null);
                                }, style: {
                                    font: 'inherit',
                                    fontSize: '0.75rem',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '999px',
                                    cursor: 'pointer',
                                    border: active
                                        ? '1px solid var(--fm-color-link)'
                                        : '1px solid var(--fm-color-border)',
                                    background: active ? 'rgba(0, 229, 255, 0.08)' : 'transparent',
                                    color: 'inherit',
                                }, children: [index + 1, ". ", done, "/", section.items.length] }, section.id));
                        }) }), _jsx("h2", { style: {
                            margin: '0 0 0.35rem',
                            fontSize: '1rem',
                            fontFamily: 'var(--fm-font-heading)',
                        }, children: currentSection.title }), _jsx("p", { style: { margin: '0 0 1rem', color: 'var(--fm-color-text-muted)' }, children: currentSection.description }), _jsx("ol", { style: {
                            margin: 0,
                            paddingLeft: 0,
                            listStyle: 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem',
                        }, children: currentSection.items.map((step) => {
                            const status = progressForStep(session.progress, step.id);
                            const href = stepHref(step, variant);
                            const isOtherApp = step.app !== 'any' && step.app !== variant;
                            const busy = busyStepId === step.id;
                            const draft = drafts[step.id] ?? '';
                            return (_jsxs("li", { style: {
                                    opacity: status === 'DONE' ? 0.78 : 1,
                                    paddingBottom: '1rem',
                                    borderBottom: '1px solid var(--fm-color-border)',
                                }, children: [_jsx("div", { style: { display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }, children: _jsxs("div", { style: { fontWeight: 600 }, children: [step.label, _jsxs("span", { style: {
                                                        marginLeft: '0.4rem',
                                                        fontWeight: 500,
                                                        fontSize: '0.75rem',
                                                        color: 'var(--fm-color-text-muted)',
                                                    }, children: [step.kind === 'question' ? 'Q' : 'Task', status === 'DONE' ? ' · done' : ''] })] }) }), _jsx("div", { style: { color: 'var(--fm-color-text-muted)', marginTop: '0.2rem' }, children: step.hint }), isOtherApp && href ? (_jsx("p", { style: { margin: '0.4rem 0 0' }, children: _jsxs("a", { href: href, style: { color: 'var(--fm-color-link)' }, children: ["Open ", otherAppLabel, " to do this"] }) })) : null, !isOtherApp && href ? (_jsx("p", { style: { margin: '0.4rem 0 0' }, children: _jsx("a", { href: href, style: { color: 'var(--fm-color-link)' }, children: "Jump there" }) })) : null, _jsxs("label", { style: { display: 'block', marginTop: '0.55rem' }, children: [_jsxs("span", { style: { fontSize: '0.8125rem', color: 'var(--fm-color-text-muted)' }, children: [step.commentPrompt, step.commentRequired || step.kind === 'question' ? ' *' : ''] }), _jsx("textarea", { value: draft, disabled: isOtherApp, onChange: (event) => {
                                                    const value = event.target.value;
                                                    setDrafts((prev) => ({ ...prev, [step.id]: value }));
                                                }, className: "fm-input", rows: 3, maxLength: 2000, style: {
                                                    display: 'block',
                                                    width: '100%',
                                                    marginTop: '0.35rem',
                                                    resize: 'vertical',
                                                } })] }), !isOtherApp ? (_jsx("div", { style: {
                                            marginTop: '0.45rem',
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '0.5rem',
                                        }, children: step.kind === 'task' && !step.commentRequired ? (_jsxs("button", { type: "button", disabled: busy, onClick: () => {
                                                void saveItem(step, {
                                                    markDone: true,
                                                    requireComment: false,
                                                });
                                            }, style: {
                                                background: 'transparent',
                                                border: 'none',
                                                color: 'var(--fm-color-link)',
                                                cursor: 'pointer',
                                                padding: 0,
                                                font: 'inherit',
                                            }, children: [status === 'DONE' ? 'Update / re-save' : 'Mark done', draft.trim() ? ' (+ comment)' : ''] })) : (_jsx("button", { type: "button", disabled: busy || !draft.trim(), onClick: () => {
                                                void saveItem(step, {
                                                    markDone: true,
                                                    requireComment: true,
                                                });
                                            }, style: {
                                                background: 'transparent',
                                                border: 'none',
                                                color: 'var(--fm-color-link)',
                                                cursor: draft.trim() ? 'pointer' : 'default',
                                                padding: 0,
                                                font: 'inherit',
                                                opacity: draft.trim() ? 1 : 0.5,
                                            }, children: status === 'DONE' ? 'Update answer' : 'Save answer' })) })) : null] }, step.id));
                        }) }), error ? (_jsx("p", { role: "alert", className: "fm-error", children: error })) : null, message ? _jsx("p", { style: { color: 'var(--fm-color-text-muted)' }, children: message }) : null, _jsxs("div", { style: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '0.75rem',
                            marginTop: '0.75rem',
                        }, children: [_jsx("button", { type: "button", disabled: sectionIndex === 0, onClick: () => {
                                    setSectionIndex((value) => Math.max(0, value - 1));
                                    setMessage(null);
                                    setError(null);
                                }, className: "fm-button", style: { opacity: sectionIndex === 0 ? 0.45 : 1 }, children: "Previous section" }), _jsx("button", { type: "button", disabled: sectionIndex >= sections.length - 1, onClick: () => {
                                    setSectionIndex((value) => Math.min(sections.length - 1, value + 1));
                                    setMessage(null);
                                    setError(null);
                                }, className: "fm-button", style: { opacity: sectionIndex >= sections.length - 1 ? 0.45 : 1 }, children: "Next section" })] })] })) : null] }));
    if (typeof document === 'undefined') {
        return panel;
    }
    return createPortal(panel, document.body);
}
//# sourceMappingURL=TesterPanel.js.map